import { ApiService } from '@sparklayer-io/spark-dash-core/types/api';
import type { ApiHandlerContract } from '@sparklayer-io/spark-dash-core/types/handlers/api';
import type { CustomerLookup } from '../types/api';
import type { Customer } from '../types/customer';
import { CustomerAddressesApi, type CustomerAddressesApiContract } from './addresses.api';

/**
 * A service contract to interact with a specific customer via the SparkLayer API.
 */
export interface CustomerApiContract {
  get(): Promise<Customer>;
  upsert(payload: Partial<Customer>): Promise<Customer>;
  delete(): Promise<void>;
  update(payload: Partial<Customer>): Promise<void>;
  addresses(): CustomerAddressesApiContract;
  addSkuToCart(sku: string): Promise<void>;
}

/**
 * A service to interact with a specific customer via the SparkLayer API.
 */
export class CustomerApi extends ApiService implements CustomerApiContract {
  constructor(
    api: ApiHandlerContract,
    private customerLookup: CustomerLookup,
  ) {
    super(api);
  }

  public get(): Promise<Customer> {
    return this.api.proxy({
      method: 'GET',
      path: '/v2/customers/{lookupBy}/{identifier}',
      params: {
        lookupBy: this.customerLookup.by,
        identifier: this.customerLookup.identifier,
      },
    });
  }

  public upsert(payload: Customer): Promise<Customer> {
    return this.api.proxy({
      method: 'PUT',
      path: '/v2/customers/{lookupBy}/{identifier}',
      payload,
      params: {
        lookupBy: this.customerLookup.by,
        identifier: this.customerLookup.identifier,
      },
    });
  }

  public delete() {
    return this.api.proxy({
      method: 'DELETE',
      path: '/v2/customers/{lookupBy}/{identifier}',
      params: {
        lookupBy: this.customerLookup.by,
        identifier: this.customerLookup.identifier,
      },
    });
  }

  public update(payload: Partial<Customer>) {
    return this.api.proxy({
      method: 'PATCH',
      path: '/v2/customers/{lookupBy}/{identifier}',
      payload,
      params: {
        lookupBy: this.customerLookup.by,
        identifier: this.customerLookup.identifier,
      },
    });
  }

  public addresses(): CustomerAddressesApiContract {
    return new CustomerAddressesApi(this.api, this.customerLookup);
  }

  // Adds one unit of `sku` to the customer's cart (creating the cart if absent), via spark-api
  // `CartController`. The `{id}` placeholder must match the proxy whitelist key verbatim.
  public addSkuToCart(sku: string): Promise<void> {
    return this.api.proxy({
      method: 'POST',
      path: '/internal/v1/customers/{lookupBy}/{id}/cart',
      payload: { sku },
      params: {
        lookupBy: this.customerLookup.by,
        id: this.customerLookup.identifier,
      },
    });
  }
}
